<script setup>
import { useAxios } from '@/hooks/useAxios';
import { useCommonCodeStore } from '@/store/commoncode-store';
import { useLoadingStore } from '@/store/loading-store';
import { computed, onMounted, ref, watch } from 'vue';

const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: ''
    },
    inputId: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    selectAll: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '2rem'
    },
    options: {
        type: Array,
        default: () => []
    },
    visible: {
        type: Boolean,
        default: true
    },
    width: {
        type: String,
        default: '12.5rem'
    },
    readonly: {
        type: Boolean,
        default: false
    },
    errmsg: {
        type: String,
        default: ''
    }
});

const loadingStore = useLoadingStore();
const selectWidth = computed(() => props.width || '12.5rem');
const isReadonly = computed(() => props.readonly);
const emit = defineEmits(['update:modelValue', 'input', 'enter', 'change', 'update-options']);
const selectedValue = ref(props.modelValue);
const errMessage = ref(props.errmsg);
const ValidationMsg = ref('');
const BrandData = ref([]);
const OriginData = ref([]);
const commonCodes = computed(() => useCommonCodeStore().getCommonCodes);
const selectedOrigin = ref('');
const filteredBrandData = ref([]);
const dataLoaded = ref(false);
let BASE_URL = import.meta.env.VITE_APP_API_URL;
const selectedOption = computed(() => {
    return BrandData.value.find((item) => item.value === selectedValue.value) || null;
});

const getImageUrl = (filename) => {
    return filename && filename.trim() !== '' ? `${BASE_URL}/view/${filename}` : `/image/icon/noimage.png`;
};

const onOriginChange = (event) => {
    selectedOrigin.value = event.value;
    filterBrandData();
};

const filterBrandData = () => {
    if (selectedOrigin.value) {
        filteredBrandData.value = BrandData.value.filter((brand) => brand.origin === selectedOrigin.value);
    } else {
        filteredBrandData.value = BrandData.value;
    }
};

// watch(() => BrandData.value, filterBrandData);
watch(
    () => selectedOrigin.value,
    (newOrigin) => {
        filteredBrandData.value = BrandData.value.filter((brand) => brand.origin === newOrigin);
        // Reset selected value in the Brand select box when origin changes
        selectedValue.value = '';
        emit('update:modelValue', ''); // Reset parent model value
    }
);

watch(
    () => props.modelValue,
    (newValue) => {
        selectedValue.value = newValue;
    }
);

watch(
    () => props.errmsg,
    (newVal) => {
        ValidationMsg.value = newVal || '';
        errMessage.value = newVal || '';
    },
    { immediate: true, deep: true }
);

const onChange = (event) => {
    ValidationMsg.value = '';
    const value = event.value;
    selectedValue.value = value;
    const selectedOption = filteredBrandData.value.find((item) => item.value === value);

    emit('change', {
        id: props.inputId,
        value: value,
        label: selectedOption ? selectedOption.label : value
    });

    emit('update:modelValue', value);
};

onMounted(async () => {
    loadingStore.showLoading();
    try {
        await Promise.all([searchBrand(), loadOriginData()]);
        dataLoaded.value = true;
    } catch (error) {
        console.error('Failed to load data:', error);
    } finally {
        loadingStore.hideLoading();
    }
});

const loadOriginData = () => {
    OriginData.value = commonCodes.value['VEHICLE_ORIGIN'].map((item) => ({
        value: item.value,
        label: item.text
    }));
};

const searchBrand = async () => {
    const config = { method: 'get' };
    try {
        const { execute } = useAxios('/api/common/VEHICLE_BRAND', config, {
            immediate: false,
            onSuccess(response) {
                const newList = response.data.data.map((item) => ({
                    value: item.code,
                    label: item.code_name,
                    filename: item.common_code.length === 0 ? '' : item.common_code[0]['attachment_filename'],
                    origin: item.attribute1
                }));
                BrandData.value = newList;
            },
            onError(err) {
                console.error('Error:', err.message);
                BrandData.value = [];
            }
        });
        await execute();
    } catch (err) {
        console.error(err);
        BrandData.value = [];
    }
};
</script>

<template>
    <div v-if="dataLoaded">
        <div v-if="visible" class="flex flex-col" :style="{ marginTop, marginBottom, marginLeft, marginRight }">
            <!-- Label Section -->
            <label :class="['label-font mb-1 mx-1', required ? 'label-required' : 'text-gray-700 dark:text-gray-300']">
                {{ label }}
                <span v-if="required" class="required-asterisk">*</span>
                <span v-if="ValidationMsg" class="validation-message">{{ ValidationMsg }}</span>
            </label>

            <div class="flex">
                <div class="custom-select w-1/3 pr-2">
                    <Select v-model="selectedOrigin" :options="OriginData" optionLabel="label" optionValue="value" placeholder="제조국" :disabled="isReadonly" class="custom-select-input" />
                </div>

                <div class="custom-select w-2/3">
                    <Select
                        :id="inputId"
                        v-model="selectedValue"
                        :options="filteredBrandData"
                        optionLabel="label"
                        optionValue="value"
                        :disabled="isReadonly"
                        @change="onChange"
                        :class="['custom-select-input', disabled ? 'disabled' : '', { 'error-border': ValidationMsg }]"
                        :placeholder="'제조국 선택 후 모델을 선택하세요'"
                        filter
                    >
                        <template #value="slotProps">
                            <div class="flex items-center">
                                <div class="w-12 h-11 flex items-center justify-center mr-2">
                                    <img v-if="selectedOption && getImageUrl(selectedOption.filename)" :src="getImageUrl(selectedOption.filename)" :alt="selectedOption.label" class="max-w-full max-h-full object-contain" />
                                </div>
                                <span>{{ selectedOption ? selectedOption.label : '' }}</span>
                            </div>
                        </template>
                        <template #option="slotProps">
                            <div class="flex items-center">
                                <div class="w-12 h-11 flex items-center justify-center mr-2">
                                    <img v-if="getImageUrl(slotProps.option.filename)" :src="getImageUrl(slotProps.option.filename)" :alt="slotProps.option.label" class="max-w-full max-h-full object-contain" />
                                </div>
                                <div>{{ slotProps.option.label }}</div>
                            </div>
                        </template>
                    </Select>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.label-font {
    font-family: 'SCoreDream600';
    height: 1.3rem;
    /* font-size: 0.875rem; */
}

.required-asterisk {
    color: #f87171; /* 빨간색 */
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.custom-select {
    position: relative;
}
.error-border {
    border-color: #f87171 !important; /* 빨간색 테두리 */
}
.custom-select-input {
    width: 100%;
    height: 3rem;
    font-size: 0.875rem;
    font-family: 'SCoreDream400';
    background-color: white;
    border-radius: 0.5rem;
    border: 1px solid #d1d5db;
    cursor: pointer;
}

.custom-select :deep(.p-select) {
    font-size: 0.875rem;
    font-family: 'SCoreDream400';
    background-color: white;
    border: 1px solid #d1d5db;
}

/* 이미지 컨테이너 스타일 추가 */
.custom-select :deep(.p-select-label) {
    padding: 0.3rem 0.2rem 0.2rem 0.5rem;
    display: flex;
    align-items: center;
}

/* 드롭다운 아이템 높이 통일 */
.custom-select :deep(.p-dropdown-item) {
    height: 3rem;
    padding: 0.5rem;
    display: flex;
    align-items: center;
}

.custom-select :deep(.p-inputwrapper-focus) {
    border-color: #0038ea !important;
}

.validation-message {
    color: #f87171; /* red-500 */
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.p-select-item:hover {
    background-color: rgba(0, 56, 234, 0.1) !important;
}

/* 필터 입력 필드 포커스 시 테두리 색상 변경 */
.p-select-filter:focus {
    border-color: #0038ea !important;
    box-shadow: 0 0 0 0.2rem rgba(0, 56, 234, 0.25) !important;
}

/* 선택된 드롭다운 항목의 배경색 변경 */
.p-select-item.p-highlight {
    background-color: rgba(0, 56, 234, 0.2) !important;
    color: #0038ea !important;
}
</style>
